/**
 * Tiptap Mermaid 扩展
 * 用于在编辑器中渲染 Mermaid 流程图
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import React, { useEffect, useRef, useState, startTransition } from 'react';
import mermaid from 'mermaid';

// 全局标记，确保 Mermaid 只初始化一次
let mermaidInitialized = false;
let mermaidInitTimer: NodeJS.Timeout | null = null;

// Mermaid 节点视图组件
const MermaidComponent = ({ node, updateAttributes, deleteNode, editor }: any) => {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const code = node.attrs.code || '';
  const containerRef = useRef<HTMLDivElement>(null);

  // 初始化 Mermaid
  useEffect(() => {
    // 如果已经初始化或正在初始化，直接返回
    if (mermaidInitialized || mermaidInitTimer) {
      return;
    }
    
    // 立即初始化 Mermaid（警告已在 index.tsx 中屏蔽）
    mermaidInitTimer = setTimeout(() => {
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
      });
      mermaidInitialized = true;
      mermaidInitTimer = null;
    }, 0);
    
    return () => {
      if (mermaidInitTimer) {
        clearTimeout(mermaidInitTimer);
        mermaidInitTimer = null;
      }
    };
  }, []);

  // 渲染 Mermaid 图表
  useEffect(() => {
    if (!code) return;

    const renderMermaid = async () => {
      try {
        const id = 'mermaid-' + Math.random().toString(36).substr(2, 9);
        const { svg: renderedSvg } = await mermaid.render(id, code);
        // 使用 startTransition 包装状态更新
        startTransition(() => {
          setSvg(renderedSvg);
          setError('');
        });
      } catch (err: any) {
        console.error('Mermaid 渲染错误:', err);
        startTransition(() => {
          setError(err.message || '渲染失败');
        });
      }
    };

    // 立即渲染
    renderMermaid();
  }, [code]);

  // 处理编辑
  const handleEdit = () => {
    if (editor.isEditable) {
      setIsEditing(true);
    }
  };

  // 保存编辑
  const handleSave = (newCode: string) => {
    updateAttributes({ code: newCode });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <NodeViewWrapper className="mermaid-editor-wrapper my-4" ref={containerRef}>
        <div className="border border-blue-300 rounded-lg p-4 bg-blue-50">
          <div className="mb-2 text-sm font-medium text-gray-700">编辑 Mermaid 代码</div>
          <textarea
            ref={(el) => {
              if (el && containerRef.current) {
                // 保存 textarea 引用
                (containerRef.current as any).textarea = el;
              }
            }}
            className="w-full h-64 p-3 border border-gray-300 rounded font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            defaultValue={code}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setIsEditing(false);
              }
            }}
            onChange={(e) => {
              // 实时更新，避免 onBlur 问题
              if (containerRef.current) {
                (containerRef.current as any).currentCode = e.target.value;
              }
            }}
            autoFocus
          />
          <div className="mt-2 flex gap-2">
            <button
              onClick={() => {
                const currentCode = (containerRef.current as any)?.currentCode || (containerRef.current as any)?.textarea?.value || code;
                handleSave(currentCode);
              }}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              保存
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              取消
            </button>
            <button
              onClick={() => deleteNode()}
              className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              删除
            </button>
          </div>
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="mermaid-wrapper" ref={containerRef}>
      <div className="my-4 border border-gray-200 rounded-lg p-4 bg-gray-50 group relative">
        {/* 编辑按钮 - 悬停显示 */}
        {editor.isEditable && (
          <button
            onClick={handleEdit}
            className="absolute top-2 right-2 px-3 py-1 text-xs bg-blue-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
          >
            📝 编辑
          </button>
        )}
        
        {error ? (
          <div className="text-red-500 text-sm p-4 border border-red-200 rounded bg-red-50">
            <div className="font-medium mb-1">渲染错误</div>
            <div>{error}</div>
            <pre className="mt-2 text-xs overflow-auto">{code}</pre>
          </div>
        ) : (
          <div
            className="mermaid-svg-container flex items-center justify-center"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
      </div>
    </NodeViewWrapper>
  );
};

// Mermaid 节点定义
export const MermaidNode = Node.create({
  name: 'mermaid',
  group: 'block',
  atom: true,

  addAttributes() {
    return {
      code: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="mermaid"]',
        getAttrs: (node) => {
          const element = node as HTMLElement;
          // 尝试从 data-mermaid-code 属性获取代码
          let code = element.getAttribute('data-mermaid-code');
          
          // 如果是编码的，解码它
          if (code) {
            try {
              code = decodeURIComponent(code);
            } catch (e) {
              console.error('解码 Mermaid 代码失败:', e);
            }
          }
          
          return { code: code || '' };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div', 
      mergeAttributes(HTMLAttributes, { 'data-type': 'mermaid' }),
      ['div', { 'data-mermaid-code': HTMLAttributes.code || '' }]
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MermaidComponent);
  },
});
